// @ts-nocheck
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { MapProvider } from 'react-map-gl/mapbox';
import { useSelector } from 'react-redux';
import { ReportingMapProvider } from 'sonata-platform-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  getCountriesWithEvents,
  getFootfallAttributionCampaigns,
  getHasReportingFootfallAttributionMapPermissions,
  getReportingType,
  getSectionsVisibility,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { MapContainer } from '@/ts-modules/reporting/Map/MapContainer';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';

type State = {
  countriesTagsStatsAvailable?: boolean;
  countryLevelWithEvents?: string;
  ready: boolean;
};

export function ReportingMap() {
  const { formatMessage } = useIntl();
  const FOOTFALL_ATTRIBUTION_MAP_ID_PATTERN = 'reportingFootfallAttributionMap';
  const countriesCartographyToken: any = {};

  const staticMode = useSelector(getStaticMode);
  const userRoles = useSelector(getUserRoles);
  const user = useSelector(getCurrentUser);
  const reportingType = useSelector(getReportingType);
  const campaignsFiltered = useSelector(getCampaigns);
  const campaignCountries = useSelector(getCountriesWithEvents) || [];
  // @ts-expect-error
  const campaignsWithFootfallAttribution = useSelector(store =>
    getFootfallAttributionCampaigns(store, campaignsFiltered)
  );
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const isDriveToStoreMap = reportingCampaigns.some(
    campaign => campaignsFiltered.includes(campaign.id) && campaign.hasDriveToStore
  );
  const hasReportingFootfallAttributionMapPermissions = useSelector(getHasReportingFootfallAttributionMapPermissions);
  const sectionsVisibility = useSelector(getSectionsVisibility);

  const hasMapPermissions = checkUserResourcePermissions('reportingMap', user, userRoles) && !staticMode;
  const hasFootfallAttributionPermissions = checkUserResourcePermissions(
    'reportingFootfallAttribution',
    user,
    userRoles
  );

  const [{ countriesTagsStatsAvailable, countryLevelWithEvents, ready }, setState] = useState<State>({
    ready: false
  });

  const showFootfallAttributionInsights =
    hasFootfallAttributionPermissions &&
    !!campaignsWithFootfallAttribution.length &&
    hasReportingFootfallAttributionMapPermissions &&
    !staticMode;

  const footfallAttributionTitle = formatMessage({
    defaultMessage: 'Footfall Attribution Map',
    id: 'reporting.footfallAttributionMap.title'
  });

  const mapTitle = formatMessage({
    defaultMessage: 'Map',
    id: 'reporting.map.title'
  });

  const getAvailableCountriesCartographyData = async () => {
    const cartographyCountryData = await getCartographyDictionary(campaignCountries, countriesCartographyToken);
    const countryLevel =
      cartographyCountryData[0].tagsConfig
        ?.filter(item => item?.reportingMapEventsEnable && item.id !== 'level0')
        .sort((a, b) => a!.order! - b!.order!)[0]?.id || 'level0';

    setState(prevState => ({
      ...prevState,
      countriesTagsStatsAvailable: cartographyCountryData.some(tagsConfig => {
        return !isEmpty(tagsConfig);
      }),
      countryLevelWithEvents: countryLevel,
      ready: true
    }));
  };

  useEffect(() => {
    if (campaignCountries.length) {
      getAvailableCountriesCartographyData();
    }
    return () => {
      countriesCartographyToken?.cancel?.();
    };
  }, [campaignCountries]);

  if (!ready) {
    return null;
  }

  return (
    <>
      {showFootfallAttributionInsights && (
        <div
          className="reportingBox"
          style={{ display: !sectionsVisibility.reportingFootfallAttributionMap ? 'none' : 'block' }}
        >
          <div
            className={`reportingShortcuts__element--${campaignsFiltered.length > 1 ? 'multi' : 'single'}`}
            id="reportingFootfallAttributionMap"
          />
          <ReportingMapProvider
            cartographyStatsAreAvailable={countriesTagsStatsAvailable}
            countryLevelWithEvents={countryLevelWithEvents}
            defaultFootfallAttributionEvent={isDriveToStoreMap ? 'fa_venue_uplift' : 'footfall_attribution'}
            idPattern={FOOTFALL_ATTRIBUTION_MAP_ID_PATTERN}
            mapType="stores"
          >
            <MapProvider>
              <MapContainer isFootfallAttrMap title={footfallAttributionTitle} />
            </MapProvider>
          </ReportingMapProvider>
        </div>
      )}
      {hasMapPermissions && (
        <div className="reportingBox" style={{ display: !sectionsVisibility.reportingMap ? 'none' : 'block' }}>
          <div className="reportingShortcuts__element" id="reportingMap" />
          <ReportingMapProvider
            cartographyStatsAreAvailable={countriesTagsStatsAvailable}
            countryLevelWithEvents={countryLevelWithEvents}
            defaultFootfallAttributionEvent={isDOOHCampaign(reportingType) ? 'impressions' : undefined}
            mapType={isDOOHCampaign(reportingType) ? 'screens' : countriesTagsStatsAvailable ? 'cartography' : 'single'}
          >
            <MapProvider>
              <MapContainer title={mapTitle} />
            </MapProvider>
          </ReportingMapProvider>
        </div>
      )}
    </>
  );
}
